import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar, SnackbarProvider } from "notistack";
import { IconButton, GlobalStyles, Box, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ButtonComponent from "../common/ButtonComponent";
import { selectSnackbarNotifications } from "../../store/snackbar/SnackbarSelectors";
import { removeSnackbar } from "../../store/snackbar/SnackbarActions";
import { updateProductSequenceList } from "../../store/product-list/ProductListActions";
import { selectSavingCategoryIds } from "../../store/product-list/ProductListSelectors";
import { useIntl } from "react-intl";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";

const ActionWrapper = styled(Box)({
  position: "absolute",
  top: "10px",
  right: "0px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  zIndex: 1,
});

const BottomActionWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "8px",
  width: "100%",
  paddingRight: "6px !important",
});

const StyledRetryButton = styled(ButtonComponent)({
  padding: "4px 12px",
  minWidth: "auto",
});

const StyledCloseButton = styled(ButtonComponent)({
  padding: "4px 12px",
  minWidth: "auto",
});

const CategorySnackbarContent: React.FC = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const intl = useIntl();
  const notifications = useSelector(selectSnackbarNotifications);

  useEffect(() => {
    notifications.forEach((notification) => {
      const action = (snackbarId: string | number) => {
        if (notification.retryPayload) {
          return (
            <>
              <BottomActionWrapper>
                <StyledCloseButton
                  variant="outlined"
                  color="secondaryButtonColorCTABlue"
                  onClick={() => closeSnackbar(snackbarId)}
                >
                  {intl.formatMessage({ id: "general.close",defaultMessage: "Close" })}
                </StyledCloseButton>
                <StyledRetryButton
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    // Dispatch the retry action with the stored payload
                    dispatch(updateProductSequenceList(notification.retryPayload!));
                    closeSnackbar(snackbarId);
                  }}
                >
                  {intl.formatMessage({ id: "snackbar.retry",defaultMessage: "Retry" })}
                </StyledRetryButton>
              </BottomActionWrapper>
            </>
          );
        } else {
          return (
            <ActionWrapper>
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={() => closeSnackbar(snackbarId)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ActionWrapper>
          );
        }
      };

      enqueueSnackbar(notification.message, {
        key: notification.key,
        variant: notification.variant,
        persist: notification.variant !== "success",
        autoHideDuration: notification.variant === "success" ? 5000 : undefined,
        action: action,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      });

      dispatch(removeSnackbar({ key: notification.key }));
    });
  }, [notifications, enqueueSnackbar, closeSnackbar, dispatch, intl]);

  return null;
};

const CategorySnackbarContainer: React.FC = () => {
  const theme = useTheme();
  const savingCategoryIds = useSelector(selectSavingCategoryIds);
  const isSaving = savingCategoryIds.length > 0;
  const bottomOffset = isSaving ? 90 : 20;
  
  const snackbarStyles = useMemo(
    () => ({
      ".notistack-SnackbarContainer": {
        bottom: `${bottomOffset}px !important`,
        transition: "bottom 0.3s ease-in-out",
      },
      ".notistack-SnackbarContainer .notistack-Snackbar": {
        maxWidth: "330px !important",
        minWidth: "330px !important",
        position: "relative",
      },
      ".notistack-SnackbarContainer .notistack-Snackbar .notistack-SnackbarContent": {
        wordWrap: "break-word",
        whiteSpace: "normal",
        overflowWrap: "break-word",
        position: "relative",
        paddingRight: "50px !important",
      },
      ".notistack-SnackbarContainer .notistack-Snackbar .notistack-SnackbarContent .notistack-message": {
        wordWrap: "break-word",
        whiteSpace: "normal",
        overflowWrap: "break-word",
        maxWidth: "100%",
      },
      "#notistack-snackbar": {
        minWidth: "280px !important",
        maxWidth: "280px !important",
        fontSize: "14px",
        fontWeight: 400,
        fontFamily: "Helvetica",
      },
      ".notistack-MuiContent-error ": {
        backgroundColor: `${theme.palette.neutral?.errorBackground} !important`,
        paddingLeft: "16px !important",
      },
      ".notistack-MuiContent-success ": { 
        backgroundColor: `${theme.palette.neutral?.successBackground} !important`,
      },
      ".notistack-MuiContent-error #notistack-snackbar,.notistack-MuiContent-error .MuiIconButton-root": {
        color: `${theme.palette.neutral?.errorTextColor} !important`,
      },
      ".notistack-MuiContent-success #notistack-snackbar,.notistack-MuiContent-success .MuiIconButton-root": {
        color: `${theme.palette.neutral?.successTextColor} !important`,
      },
      ".notistack-MuiContent-success  #notistack-snackbar svg": {
        color: `${theme.palette.neutral?.green} !important`,
      },
      ".notistack-MuiContent-error #notistack-snackbar svg": {
        color: `${theme.palette.neutral?.errorIconColor} !important`,
      },
      ".notistack-MuiContent-success svg, .notistack-MuiContent-error svg": {
        marginRight: "8px",
      },
      ".notistack-MuiContent-error #notistack-snackbar, .notistack-MuiContent-success #notistack-snackbar": {
        display: "flex",
        alignItems: "flex-start",
      },
    }),
    [bottomOffset, theme],
  );
  
  return (
    <>
      <GlobalStyles styles={snackbarStyles} />
      <SnackbarProvider
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        iconVariant={{
          success: <CheckCircleOutline fontSize="small" />,
          error: <ErrorOutline fontSize="small" />,
        }}
      >
        <CategorySnackbarContent />
      </SnackbarProvider>
    </>
  );
};

export default CategorySnackbarContainer;
